import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TenantService } from './tenant.service';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { catchError, finalize, of } from 'rxjs';
import { PortalService } from './portal.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private tenantService: TenantService,
    private portalService: PortalService,
    private router: Router,
    private api: ApiService
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private get tokenKey(): string {
    return 'access_token';
  }

  private get roleKey(): string {
    return `role_${this.tenantService.id}`;
  }

  private get displayNameKey(): string {
    return `display_name_${this.tenantService.id}`;
  }

  getTokenSafe(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getTokenSafe();
  }

  getCurrentUserRole(): 'admin' | 'client' | null {
    if (!this.isBrowser()) return null;
    return (localStorage.getItem(this.roleKey) as any) ?? null;
  }

  getDisplayName(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem(this.displayNameKey);
  }

  setToken(token: string) {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.tokenKey, token);
  }

  setRole(role: 'admin' | 'client' | string) {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.roleKey, role);
  }

  setDisplayName(name: string) {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.displayNameKey, name);
  }

  login(params: { email: string; password: string; portal_id?: string }) {
    const portal = this.portalService.portal();
    const payload = {
      email: params.email,
      password: params.password,
      portal_id: portal.id || null,
    };

    return this.api.post('/api/v1/login', payload);
  }

  changePassword(params: { currentPassword: string; newPassword: string; repeatPassword: string }) {
    const payload = {
      current_password: params.currentPassword,
      new_password: params.newPassword,
      confirm_password: params.repeatPassword,
    };

    return this.api.post('/api/v1/change-password', payload);
  }

  loginMock(role: 'admin' | 'client') {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.tokenKey, 'dummy-token');
    localStorage.setItem(this.roleKey, role);
  }

  private clearSession() {
    if (!this.isBrowser()) return;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.roleKey);
    localStorage.removeItem(this.displayNameKey);
  }

  forceLogout() {
    if (!this.isBrowser()) return;
    this.clearSession();
    this.router.navigate(['/login']);
  }

  logoutWithoutRedirect() {
    if (!this.isBrowser()) {
      return of(null);
    }

    return this.api.post('/api/v1/logout', {}).pipe(
      catchError(() => of(null)),
      finalize(() => {
        this.clearSession();
      })
    );
  }

  logout() {
    if (!this.isBrowser()) {
      return of(null);
    }

    return this.api.post('/api/v1/logout', {}).pipe(
      catchError(() => of(null)),
      finalize(() => {
        this.clearSession();
        this.router.navigate(['/login']);
      })
    );
  }
}
